import { useLocalStorage } from "@rehooks/local-storage";
import _toPairs from "lodash/toPairs";
import React from "react";
import styled, { css } from "styled-components";
import { Button } from "./Button";
import { Icon } from "./Icon";
import { ALERT_TYPES, AlertType } from "./constants/alerts";
import { LoadingState } from "./states/LoadingState";
import { mediaQueries, style } from "./style";
import { focusOutline } from "./utils/focusOutlineStyle";

type AlertProps = {
  type: AlertType | "info" | "success" | "warning" | "error" | "loading";
  children: React.ReactNode;
  className?: string;
  title?: React.ReactNode;
  buttons?: {
    primary?: ButtonProps;
    secondary?: ButtonProps;
  };
  onClose?: () => void;
  inline?: boolean;
  icon?: React.ReactNode;
};

type AlertContainerProps = {
  type: AlertType;
  inline?: boolean;
};

type ButtonProps = {
  label: string;
  action: () => void;
};

type DescriptionContainerProps = {
  hasTitle: boolean;
};

// This could be moved out and shared once we convert the toast notifications to this style. (PDH-340)

const MAX_CHAR_WIDTH = 75;
const MAX_CHAR_TOTAL = 250;

export const AlertContainer = styled.div<AlertContainerProps>`
  ${({ type }) => css`
    background-color: ${ALERT_TYPES[type].bgColor};
  `}

  ${({ type, inline }) =>
    !inline &&
    css`
      border: 1px solid ${ALERT_TYPES[type].borderColor};
    `}

  align-items: flex-start;
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  padding: ${style.vars.space["3"]};
  border-radius: ${style.vars.borderRadius.base};

  &:focus {
    ${focusOutline()}
  }
`;

const IconStyles = css`
  margin-right: ${style.vars.space["3"]};
  margin-bottom: ${style.vars.space["3"]};
  flex-grow: 0;
`;

const AlertIcon = styled(Icon)`
  ${IconStyles}
`;

const Spinner = styled(LoadingState)`
  ${IconStyles};
  margin-left: ${style.vars.space["1"]};
  margin-top: ${style.vars.space["1"]};
`;

const Title = styled.h4`
  margin-top: 0;
  margin-bottom: 0;

  ${mediaQueries.mediumMin} {
    margin-top: ${style.vars.space["1"]};
  }
`;

const InlineTitle = styled.p`
  margin-top: 0;
  margin-bottom: 0;
  font-weight: ${style.vars.fontWeight.bold};
`;

const DescriptionContainer = styled.div<DescriptionContainerProps>`
  margin-top: ${({ hasTitle }) => (hasTitle ? style.vars.space["4"] : 0)};
  margin-bottom: ${({ hasTitle }) => (hasTitle ? style.vars.space["4"] : 0)};
`;

const CopyContainer = styled.div`
  margin-right: ${style.vars.space["4"]};
  z-index: ${style.vars.zIndex.base};
  width: 100%;

  ${mediaQueries.mediumMin} {
    max-width: ${MAX_CHAR_WIDTH}ch;
    flex: 1 1 0;
  }
`;

const PrimaryButton = styled(Button)`
  margin-right: ${style.vars.space["3"]};

  ${mediaQueries.smallMin} {
    margin-bottom: ${style.vars.space["2"]};
  }
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: ${style.vars.zIndex.base};
`;

const CloseButton = styled(Button)`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: ${style.vars.space["1"]};
  margin-left: ${style.vars.space["3"]};
`;

export const Alert: React.FC<AlertProps> = ({ type, title, children, onClose, buttons, className, inline, icon, ...props }) => {
  let truncatedString;

  // Truncate string if it's more than 250 characters
  if (typeof children === "string" && children.length > MAX_CHAR_TOTAL) {
    truncatedString = `${children.substring(0, MAX_CHAR_TOTAL)}...`;
  }

  let titleElement;
  if (title && inline) {
    titleElement = <InlineTitle>{title}</InlineTitle>;
  } else if (title) {
    titleElement = <Title>{title}</Title>;
  }

  return (
    <AlertContainer
      className={className}
      type={type as AlertType}
      inline={inline}
      role={ALERT_TYPES[type].role}
      tabIndex={0}
      aria-live="polite"
      aria-label={typeof title === "string" ? title : undefined}
      {...props}
    >
      {type === "loading" ? (
        <Spinner />
      ) : typeof icon === "string" || typeof icon === "undefined" ? (
        <AlertIcon icon={icon || ALERT_TYPES[type].icon} xlarge={!inline} large={inline} />
      ) : (
        icon
      )}

      {onClose && (
        <CloseButtonContainer aria-label="close button">
          <CloseButton aria-label="Close" variation="icon" icon="close" onClick={onClose} />
        </CloseButtonContainer>
      )}

      <CopyContainer>
        {titleElement}

        {children && <DescriptionContainer hasTitle={!!title}>{truncatedString ? truncatedString : children}</DescriptionContainer>}

        {buttons && (
          <>
            {buttons.primary && (
              <PrimaryButton variation="banner" onClick={buttons.primary.action}>
                {buttons.primary.label}
              </PrimaryButton>
            )}
            {buttons.secondary && (
              <Button variation="banner-secondary" onClick={buttons.secondary.action}>
                {buttons.secondary.label}
              </Button>
            )}
          </>
        )}
      </CopyContainer>
    </AlertContainer>
  );
};

type useAlertResult = {
  isAlertHidden: boolean;
  hideAlert: () => void;
  setIsAlertHidden: React.Dispatch<boolean>;
};

export function useAlert(
  alertName: string,
  props?: {
    [key: string]: string;
  }
): useAlertResult {
  const pairs = _toPairs(props);
  const baseKey = [alertName, pairs.map((item) => item.join("-")).join("-")].filter(Boolean).join("-");

  const [isAlertHidden, setIsAlertHidden] = useLocalStorage<boolean>(`${baseKey}-hidden`, false);

  const hideAlert = (): void => {
    setIsAlertHidden(true);
  };

  return { isAlertHidden, hideAlert, setIsAlertHidden };
}
